import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { defaultFeedbackToastComponentProps } from "../cubeComponent/constants";
import type { Exact } from "@Helpers/types/exact";
import type { TranslatedString } from "@i18n";

/**
 * Toast notification payload type.
 * The `content` field requires a TranslatedString to ensure all messages are internationalized.
 * Use the `t()` function from @i18n to create translated content.
 *
 * @example
 * import { t } from "@i18n";
 * dispatch(showToastNotification({ content: t("success.message"), type: "success" }));
 */
export interface ToastNotificationPayload {
  content: TranslatedString;
  type?: string | undefined;
  title?: string | undefined;
  timeOut?: number | undefined;
  isActionButton?: boolean | undefined;
  actionBtnText?: string | undefined;
  onActionBtnClick?: (() => void) | undefined;
  position?:
    | {
        X: string;
        Y: string;
      }
    | undefined;
}

export interface ToastNotificationState extends Partial<ToastNotificationPayload> {}

const toastSlice = createSlice({
  name: "toastNotificationContent",
  initialState: {} as ToastNotificationState,
  reducers: {
    setToastNotification: (_state: ToastNotificationState, action: PayloadAction<ToastNotificationPayload>) =>
      // An empty `content` still clears the toast: it is what the notification renders, so a blank
      // one would show an empty box rather than a message.
      action.payload.content ? { ...defaultFeedbackToastComponentProps, ...action.payload } : {},
  },
});

const { setToastNotification: _setToastNotificationRaw } = toastSlice.actions;

/** Exact<>-wrapped to catch excess properties even when payload comes from a variable. */
export const setToastNotification = <T extends Exact<ToastNotificationPayload, T>>(payload: T) =>
  _setToastNotificationRaw(payload);
export const showToastNotification = setToastNotification;
export default toastSlice.reducer;
